import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

/** Gradiente utilitario (mismo patrón que usas en otros selectores) */
const makeBg = (start, end) =>
  end ? `linear-gradient(135deg, ${start} 8%, ${end} 92%)` : start;

/** Contenedor centrado y responsive (idéntico a los otros) */
const containerSx = (hideBelowWidth = 0) => ({
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  mt: -1,
  opacity: 1,
  transition: "opacity .25s ease",
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

// Números un pelín más finos/pequeños + unidad "m" más pequeña
function renderLabelThinNumsAndUnit(label) {
  const parts = String(label).split(/(\d+(?:[.,]\d+)?)/g); // separa 0.5, 600, 1000, etc.
  return parts.map((part, i) => {
    const isNumber = /\d/.test(part);

    // dentro de los trozos no numéricos, estiliza la "m" como unidad
    if (!isNumber) {
      return part.split(/(m)/g).map((sub, j) =>
        sub === 'm' ? (
          <Box
            key={`${i}-${j}`}
            component="span"
            sx={{ fontSize: '0.85em', opacity: 0.9, lineHeight: 1 }}
          >
            m
          </Box>
        ) : (
          <React.Fragment key={`${i}-${j}`}>{sub}</React.Fragment>
        )
      );
    }

    // estilo para los números
    return (
      <Box
        key={i}
        component="span"
        sx={{
          fontWeight: 450,               // un poco más visible
          fontSize: '0.92em',            
          letterSpacing: '.005em',
          fontVariantNumeric: 'tabular-nums', 
          lineHeight: 1,
        }}
      >
        {part}
      </Box>
    );
  });
}

/* Texto blanco/negro automático por contraste (YIQ) */
function textColorFor(hex) {
  if (!hex || hex.length < 6) return "#0B1220";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0B1220" : "#FFFFFF";
}

/** Paleta Visibility (4 niveles) + color de texto recomendado por contraste */
const LEVELS = [
  // > 1000 m (mejor visibilidad) — verde
  { label: "> 1000", code: "V4", start: "#43A047", end: "#2E7D32", text: "#FFFFFF" },
  // 600–1000 m — azul
  { label: "600–1000", code: "V3", start: "#1E88E5", end: "#1976D2", text: "#FFFFFF" },
  // 350–600 m — amarillo (texto oscuro para legibilidad)
  { label: "350–600", code: "V2", start: "#FBC02D", end: "#FDD835", text: "#0B1220" },
  // < 350 m (peor visibilidad) — rojo
  { label: "< 350", code: "V1", start: "#E53935", end: "#D32F2F", text: "#FFFFFF" },
];

export default function RiskSelectorVisibility({
  onSelectRiskLevel,
  hideBelowWidth = 0,
  interactive = false,
  size = "mini",           // "mini" | "md"
  equalWidth = true,
  showTitle = true,
  titleText = "Visibility (mts)",
}) {
  const [selectedRisk, setSelectedRisk] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTooltipOpen(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const isMini = size === "mini";
  const pillMin = equalWidth ? (isMini ? 92 : 108) : 0;

  return (
    <Box>
      {/* Título sutil centrado con icono (mismo estilo tipográfico) */}
      {showTitle && (
        <Box sx={{ display: "grid", justifyItems: "center", pointerEvents: "none", mb: 0.95 }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              typography: "caption",
              fontWeight: 800,
              letterSpacing: ".025em",
              textTransform: "uppercase",
              color: "#23339b",
              lineHeight: 1.05,
            }}
          >
            <VisibilityRoundedIcon sx={{ fontSize: { xs: 12, sm: 12, md: 13 }, color: "inherit" }} aria-hidden />
            {titleText}
          </Box>
        </Box>
      )}

      {/* Cuatro casillas simétricas (estilo idéntico) */}
      <Box sx={containerSx(hideBelowWidth)}>
        <Box
          sx={{
            display: "inline-flex",
            borderRadius: 999,
            overflow: "hidden",
            pointerEvents: interactive ? "auto" : "none",
          }}
        >
          {LEVELS.map((lvl, i) => {
            const isSelected = selectedRisk === lvl.code;
            return (
              <Tooltip
                key={lvl.code}
                title={`${lvl.label} (${lvl.code})`}
                arrow
                placement="top"
                disableInteractive
                open={i === 0 ? tooltipOpen : false}
              >
                <Button
                  disableElevation
                  disableRipple
                  aria-label={lvl.label}
                  onClick={
                    interactive
                      ? () => {
                          setSelectedRisk(lvl.code);
                          onSelectRiskLevel?.(lvl.code);
                        }
                      : undefined
                  }
                  sx={{
                    position: "relative",
                    minWidth: equalWidth ? (isMini ? 78 : 92) : 0,
                    px: isMini ? 1.0 : 1.2,
                    py: isMini ? 0.44 : 0.58,
                    fontSize: isMini ? ".62rem" : ".7rem",
                    fontWeight: 600,
                    letterSpacing: ".02em",
                    lineHeight: 1,
                    textTransform: "none",
                    color: textColorFor(lvl.start),
                    backgroundColor: `rgba(${parseInt(lvl.start.slice(1, 3), 16)}, ${parseInt(lvl.start.slice(3, 5), 16)}, ${parseInt(lvl.start.slice(5, 7), 16)}, 0.8)`,
                    backdropFilter: "blur(20px) saturate(190%) contrast(1.1)",
                    borderRadius: 0,
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                    borderTop: "2.5px solid rgba(255, 255, 255, 0.5)",
                    borderRight: i < LEVELS.length - 1 ? 0 : "1px solid rgba(255, 255, 255, 0.25)",
                    boxShadow: `
                      0 12px 40px 0 rgba(0, 0, 0, 0.25),
                      inset 0 1px 1px 0 rgba(255, 255, 255, 0.3),
                      inset 0 -1px 0px 0 rgba(0, 0, 0, 0.1)
                    `,
                    transition: "transform .2s cubic-bezier(0.4, 0, 0.2, 1), background-color .25s ease, box-shadow .2s ease",
                    cursor: interactive ? "pointer" : "default",
                    "&:hover": {
                      transform: interactive ? "scale(1.02) translateY(-2px)" : "none",
                      backgroundColor: `rgba(${parseInt(lvl.start.slice(1, 3), 16)}, ${parseInt(lvl.start.slice(3, 5), 16)}, ${parseInt(lvl.start.slice(5, 7), 16)}, 0.9)`,
                      boxShadow: "0 15px 45px 0 rgba(0, 0, 0, 0.3)",
                      zIndex: 2,
                    },
                  }}
                >
                  {renderLabelThinNumsAndUnit(lvl.label)}
                </Button>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
